/**
 * Legacy parity: AjaxAdd.aspx.cs parent bundle path (mixed child stock).
 */

import { EBOOK_BUNDLE_SENTINEL } from '@/ebookBundle'

export type GroupChildProductForCart = {
  id: number
  name?: string | null
  isPreorder?: boolean | null
  options?: Array<{
    id?: number | null
    inStock: boolean
    productType?: { name?: string | null } | null
    price?: number
    salePrice?: number | null
  }> | null
}

function isNonComponentOption(opt: {
  productType?: { name?: string | null } | null
}): boolean {
  const n = (opt.productType?.name ?? '').toLowerCase()
  return n !== 'component'
}

/** Matches legacy: in stock, or child preorder while parent is preorder. */
export function isChildPurchasableForGroupAdd(
  child: GroupChildProductForCart,
  parentIsPreorder: boolean
): boolean {
  const opts = (child.options ?? []).filter(isNonComponentOption)
  if (opts.length === 0) return false
  return opts.some(
    o =>
      o.inStock ||
      (Boolean(child.isPreorder) && parentIsPreorder)
  )
}

export function firstPurchasableChildOptionId(
  child: GroupChildProductForCart,
  parentIsPreorder: boolean
): number | null {
  for (const o of child.options ?? []) {
    if (!isNonComponentOption(o)) continue
    const ok =
      o.inStock || (Boolean(child.isPreorder) && parentIsPreorder)
    if (ok && o.id != null && o.id !== 0) return o.id
  }
  return null
}

export function analyzeGroupChildCart(
  children: GroupChildProductForCart[] | undefined | null,
  parentIsPreorder: boolean
): {
  partialGroupAdd: boolean
  allChildrenPurchasable: boolean
  purchasableChildren: GroupChildProductForCart[]
  oosChildren: GroupChildProductForCart[]
} {
  const list = children ?? []
  const purchasableChildren = list.filter(c =>
    isChildPurchasableForGroupAdd(c, parentIsPreorder)
  )
  const oosChildren = list.filter(
    c => !isChildPurchasableForGroupAdd(c, parentIsPreorder)
  )
  const partialGroupAdd =
    list.length > 0 &&
    purchasableChildren.length > 0 &&
    oosChildren.length > 0

  return {
    partialGroupAdd,
    allChildrenPurchasable:
      list.length > 0 &&
      oosChildren.length === 0 &&
      purchasableChildren.length === list.length,
    purchasableChildren,
    oosChildren,
  }
}

function formatOosNameList(names: string[]): string {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  const last = names[names.length - 1]
  return `${names.slice(0, -1).join(', ')} and ${last}`
}

export function formatPartialGroupCartNotice(
  oosChildren: GroupChildProductForCart[]
): string {
  const names = oosChildren.map(
    c => (c.name && c.name.trim()) || `Product #${c.id}`
  )
  const n = names.length
  const label = n === 1 ? 'a product' : `${n} products`
  const verb = n === 1 ? 'is' : 'are'
  const list = formatOosNameList(names)
  return `Unfortunately, ${label} in this group ${verb} out of stock: ${list}. We have added the other products to your cart though.`
}

/**
 * Determines if clicking "Add to cart" on parent should add available children instead.
 *
 * Scenarios where we offer group child add:
 * 1. Parent has no purchasable options but children are available (bundle wrapper only)
 * 2. Parent has 1 option with mixed child stock (partial fulfillment)
 * 3. Parent has 1 option that's OOS but all children are in stock (buy individually)
 */
export function shouldOfferPartialGroupChildAdd(args: {
  groupChildProducts: GroupChildProductForCart[] | undefined | null
  parentIsPreorder: boolean
  parentOptions: { id: number }[]
}): boolean {
  const { groupChildProducts, parentIsPreorder, parentOptions } = args
  const { partialGroupAdd, purchasableChildren } = analyzeGroupChildCart(
    groupChildProducts,
    parentIsPreorder
  )

  // No children available at all
  if (purchasableChildren.length === 0) return false

  // Multiple parent options - user should select specific option first
  if ((parentOptions?.length ?? 0) > 1) return false

  // Ebook bundle sentinel - special handling elsewhere
  if (
    parentOptions.length === 1 &&
    parentOptions[0].id === EBOOK_BUNDLE_SENTINEL
  ) {
    return false
  }

  // Case 1: Parent has no options - offer child add if any children available
  if (parentOptions.length === 0) {
    return true
  }

  // Case 2: Parent has single option but partial stock - offer child add
  if (partialGroupAdd) return true

  // Case 3: Parent has 1 option, all children purchasable - check if parent option is in stock
  // Note: We can't check parent stock here (not in args), so we'll handle in AddToCartButton
  // For now, only trigger if there's partial stock (case 2 above)

  return false
}
